import { el } from './dom';

/**
 * Shown once, immediately after a no-email signup.
 *
 * This is the only screen in the game a player must actually act on, and the
 * consequence of ignoring it is permanent: we store only a bcrypt hash, so
 * nobody — including us — can recover the code later. The copy says that
 * plainly rather than softening it, and Continue stays disabled until the
 * player ticks the box, because a ten-year-old will otherwise click straight
 * past it.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §5.
 */
export function buildRecoveryCodePanel(code: string, onDone: () => void): HTMLDivElement {
  const codeBox = el('div', { className: 'recovery-code', testid: 'recovery-code' }, code);

  const copy = el('button', { className: 'account-secondary', testid: 'recovery-copy' }, 'Copy');
  const copied = el('span', { className: 'account-notice', testid: 'recovery-copied' });
  copy.addEventListener('click', () => {
    void navigator.clipboard
      ?.writeText(code)
      .then(() => {
        copied.textContent = 'Copied.';
      })
      .catch(() => {
        // Clipboard is blocked in some browsers; the code is on screen anyway.
        copied.textContent = 'Write it down instead.';
      });
  });

  const print = el('button', { className: 'account-secondary', testid: 'recovery-print' }, 'Print');
  print.addEventListener('click', () => window.print());

  const saved = el('input', { type: 'checkbox', testid: 'recovery-saved' });
  const continueBtn = el('button', { testid: 'recovery-continue' }, 'Continue');
  continueBtn.disabled = true;
  saved.addEventListener('change', () => {
    continueBtn.disabled = !saved.checked;
  });
  continueBtn.addEventListener('click', () => {
    if (saved.checked) onDone();
  });

  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-recovery' },
    el('h2', {}, 'Your secret code'),
    el(
      'div',
      { className: 'account-hint' },
      'Write this down and keep it somewhere safe. Tell a grown-up where you put it.',
    ),
    codeBox,
    el('div', { className: 'recovery-actions' }, copy, print, copied),
    el(
      'div',
      { className: 'recovery-warning', testid: 'recovery-warning' },
      'This is the only way back into your account if you forget your password. ' +
        'We keep it scrambled, so even we cannot read it — nobody can steal it from ' +
        'us, but nobody can get it back for you either. Lose it and the account is gone.',
    ),
    el('label', { className: 'recovery-confirm' }, saved, ' I have saved my code'),
    continueBtn,
  );
}
